import { useEffect, useRef, useState } from 'react';
import cx from 'classnames';

import { Checkbox } from '../checkbox';
import { Field, controlClasses, ControlSize, controlSizeClasses, useField } from '../field';
import { Pressable } from '../pressable';
import { Arrow } from '../icons/arrow';

export type MultiSelectItem = { id: string; name: string };

export interface MultiSelectProps {
  size: ControlSize;
  label?: string;
  items: MultiSelectItem[];
  /** Ids of the chosen items. */
  value: string[];
  onChange: (value: string[]) => void;
  placeholder?: string;
  error?: string;
}

const Trigger = ({
  size,
  open,
  text,
  placeholder,
  onToggle,
}: {
  size: ControlSize;
  open: boolean;
  text: string;
  placeholder?: string;
  onToggle: () => void;
}) => {
  const field = useField();
  return (
    <Pressable
      id={field?.id}
      aria-expanded={open}
      aria-invalid={field?.invalid || undefined}
      aria-describedby={field?.describedBy}
      onClick={onToggle}
      className={cx(
        controlClasses(Boolean(field?.invalid)),
        controlSizeClasses[size],
        'flex items-center gap-2 text-left',
      )}
    >
      <span className={cx('min-w-0 flex-1 truncate', !text && 'text-ink-subtle')}>{text || placeholder}</span>
      <Arrow className={cx('w-3 shrink-0 transition-transform', open && 'rotate-180')} />
    </Pressable>
  );
};

/** Several values out of a list, each with a checkbox; the field shows the chosen names. */
export const MultiSelect = ({ size, label, items, value, onChange, placeholder, error }: MultiSelectProps) => {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const onMouseDown = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', onMouseDown);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('mousedown', onMouseDown);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [open]);

  const toggle = (id: string) => onChange(value.includes(id) ? value.filter((v) => v !== id) : [...value, id]);

  const chosen = value.map((id) => items.find((item) => item.id === id)?.name ?? id).join(', ');

  return (
    // The list stays outside `Field`: its checkboxes would take the field's error as their own.
    <div className="relative w-full" ref={ref}>
      <Field size={size} label={label} error={error}>
        <Trigger size={size} open={open} text={chosen} placeholder={placeholder} onToggle={() => setOpen((v) => !v)} />
      </Field>
      {open && (
        // `list-none!`: styles/index.css sets `ul { list-style: initial }` outside any layer.
        <ul className="absolute left-0 right-0 top-full z-10 mt-1 flex max-h-52 list-none! flex-col overflow-y-auto rounded-control border border-line-strong bg-surface p-2 shadow-popup">
          {items.map((item) => (
            <li key={item.id}>
              <Checkbox
                size="sm"
                label={item.name}
                checked={value.includes(item.id)}
                onChange={() => toggle(item.id)}
                className="w-full rounded-chip px-3 py-2.5 hover:bg-accent-soft"
              />
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};
